import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DxDataGridComponent } from 'devextreme-angular';
import { UserApi } from '../../../api';
import { FilterBuilderService } from '../../../service/filter-builder.service';
import { LoaderService } from '../../../core/loader/loader.service';
import { CountryDataService } from '../../../api/country-data.service';
import { ExtraFilteringOptions, LoadOptions } from '../../../shared/models/shared.model';
import { Subject } from 'rxjs';
import {GridSetting, GridSettings, GridState, User} from '../../../shared';
import { CountryMappingOption } from '../../../shared/models/country-data.model';
import { MappingAction, MappingMode } from '../../../shared/enum/mapping-mode.enum';
import {take, takeUntil} from 'rxjs/operators';
import CustomStore from 'devextreme/data/custom_store';

@Component({
  selector: 'app-country-mapping',
  templateUrl: 'country-mapping.component.html'
})

export class CountryMappingComponent implements OnInit, OnDestroy {
  @ViewChild('countryMappingDataGrid') countryMappingDataGrid: DxDataGridComponent;

  public countryMappingDataSource = {};
  public countryMappingSelectedRowMap = [];
  public currentUser: User;

  public sourceCountryHeaderDataSource;
  public dataProviderCodeHeaderDataSource;
  public dataProviderShortNameHeaderDataSource;
  public dataProviderPrimaryOrDefaultCountryGeographicalEntityCountryAlpha2CodeHeaderDataSource;
  public isDataProviderPrimaryOrDefaultCountryInconsistentMappingMessageHeaderDataSource;
  public mappingModeNameHeaderDataSource;
  public countryShortNameHeaderDataSource;
  public countryAlpha2CodeNameHeaderDataSource;
  public reportListHeaderDataSource;
  public createdByHeaderDataSource;
  public modifiedByHeaderDataSource;
  public reviewedByHeaderDataSource;
  public reviewedOnHeaderDataSource;
  public createdOnHeaderDataSource;
  public modifiedOnHeaderDataSource;

  public extraFilteringOptions: ExtraFilteringOptions[] = [
	{ column: 'Active', value: false },
  ];

  private _destroyed$ = new Subject();
  private isCountryMappingDataLoading: boolean;
  private mappingDataGridCurrentPage: number;

  public isLoadCountryMasterGridDataSource: boolean;
  private readonly masterGridName: string = 'countryMasterDataGrid';
  private readonly pageName: string = 'countryMapping';
  private currentCountryMasterDataGridState: GridState;
  private countryMasterDataGridSettings: GridSettings;
  private isCountryMasterDataGridSettingLoading = true;
  private countryMasterDataGridSetting: GridSetting = new GridSetting();

  constructor(
	private countryDataService: CountryDataService,
	private userService: UserApi,
	private filterBuilder: FilterBuilderService,
	private loaderService: LoaderService,
  ) {
  }

  ngOnInit() {
	this.loaderService.show();
	this.currentUser = this.userService.logonUser;
	if (!this.currentUser) {
		return;
	}
	this.countryMasterDataGridSettings = {
		PageName: this.pageName,
		GridName: this.masterGridName,
		OwnedByUserId: this.currentUser.UserId
	};

	this.userService.getGridSettings(this.countryMasterDataGridSettings)
		.pipe(take(1))
		.subscribe((settings) => {
		if (settings && settings.Settings) {
			if (typeof settings.Settings === 'string') {
			this.countryMasterDataGridSetting = JSON.parse(settings.Settings);
			} else {
			this.countryMasterDataGridSetting = settings.Settings;
			}
		} else {
			this.countryMasterDataGridSettings.Settings = JSON.stringify({autosave: true});
			this.updateGridSettings(this.countryMasterDataGridSettings);
		}
		this.loadCountryMasterDataGridState();
		});

	this.headerFilterDataSets();
  }

  private loadCountryMappingDataSource() {
	this.isCountryMasterDataGridSettingLoading = false;
	this.countryMappingDataSource = new CustomStore({
		key: 'CountryMappingId',
		load: (loadOptions: any) => {
		this.isCountryMappingDataLoading = true;
		this.loaderService.show();
		const filter = this.formatFilterOperators(loadOptions.filter);
		const finalOptions: LoadOptions = loadOptions;
		finalOptions.filter = filter;
		finalOptions.extraParams = this.extraFilteringOptions;

		return this.countryDataService.getCountryMappingGrid(finalOptions)
			.toPromise()
			.then((data: any) => {
			this.isCountryMappingDataLoading = false;
			this.loaderHandler();
			return {
				data: data.data,
				totalCount: data.totalCount,
				summary: data.summary,
				groupCount: data.groupCount
			};
			})
			.catch(error => {
			this.isCountryMappingDataLoading = false;
			this.loaderHandler();
			throw new Error('Data Loading Error');
			});
		}
	});
	this.isLoadCountryMasterGridDataSource = true;
  }

  private loadCountryMasterDataGridState() {
	const countryMasterDataGridState: GridState = {
		PageName: this.pageName,
		GridName: this.masterGridName,
		OwnedByUserId: this.currentUser.UserId
	};
	this.userService.getGridState(countryMasterDataGridState)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((state) => {
		if (state) {
			this.currentCountryMasterDataGridState = state;
		} else {
			this.currentCountryMasterDataGridState = {
			PageName: this.pageName,
			GridName: this.masterGridName,
			OwnedByUserId: this.currentUser.UserId,
			State: null
			};
		}
		this.loadCountryMappingDataSource();
		});
  }

  public loadMasterState = () => {
	return this.currentCountryMasterDataGridState.State ? JSON.parse(this.currentCountryMasterDataGridState.State) : null;
  }

  public saveMasterState = (state) => {
	if (this.countryMasterDataGridSetting) {
		let gridSetting: GridSetting;
		if (typeof this.countryMasterDataGridSetting === 'string') {
		gridSetting = JSON.parse(this.countryMasterDataGridSetting);
		} else {
		gridSetting = this.countryMasterDataGridSetting;
		}
		if (gridSetting.autosave) {
		this.currentCountryMasterDataGridState.State = JSON.stringify(state);
		this.updateGridState(this.currentCountryMasterDataGridState);
		}
	}
  }

  private updateGridSettings(gridSettings: GridSettings) {
	gridSettings.SavedOn = new Date();
	this.userService.setGridSettings(gridSettings).pipe(take(1)).subscribe();
  }

  private updateGridState(gridState: GridState) {
	gridState.SavedOn = new Date();
	this.userService.setGridState(gridState).pipe(take(1)).subscribe();
  }

  extraFilteringOptionsChange(columnName, event) {
	if (event) {
		const option = this.extraFilteringOptions.find(item => item.column === columnName);
		option.value =  event.value;
		this.countryMappingDataGrid.instance.deselectAll();
		this.refreshCompanyMappingDataGrid();
	}
  }

  refreshCompanyMappingDataGrid() {
	this.mappingDataGridCurrentPage = this.countryMappingDataGrid.instance.pageIndex();
	this.countryMappingDataGrid.instance.refresh();
  }

  selectionMappingDataChangedHandler() {
	if (this.countryMappingDataGrid.instance.getSelectedRowsData()[0]) {
		this.countryMappingSelectedRowMap = this.countryMappingDataGrid.instance.getSelectedRowsData()[0];
	}
  }

  onReady(e) {
	const grid = e.component;
	const selection = grid.getSelectedRowKeys();
	if (selection.length === 0) {
		grid.selectRowsByIndexes([0]);
	}
  }

  countryMappingDataGridContextMenu(e) {
	if (e.target === 'content') {
		// e.items can be undefined
		if (!e.items) { e.items = []; }

		// Add a custom menu item
		e.items.push(
		{
			text: 'Refresh',
			icon: 'refresh',
			onItemClick: () => {
			this.refreshCompanyMappingDataGrid();
			}
		},
		{
			text: 'Grid Settings',
			items: [
			{
				text: this.countryMasterDataGridSetting.autosave ? 'Disable autosave' : 'Enable autosave',
				onItemClick: () => {
				this.countryMasterDataGridSetting.autosave = !this.countryMasterDataGridSetting.autosave;
				this.countryMasterDataGridSettings.Settings = JSON.stringify(this.countryMasterDataGridSetting);
				this.updateGridSettings(this.countryMasterDataGridSettings);
				}
			}
			]
		}
		);
	}
  }

  customizeCountryMasterId(cellInfo) {
	const idStr = 'CnMp_000000000';
	const cellValue = String(cellInfo.value);
	const index = idStr.length - cellValue.length;
	const finalText = idStr.slice(0, index) + cellValue;
	return finalText;
  }

  private headerFilterDataSets() {
	this.countryShortNameHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'CountryShortName';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCountryMapping(loadOptions);
		}
	});

	this.sourceCountryHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'SourceCountry';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCountryMapping(loadOptions);
		}
	});

	this.dataProviderCodeHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'DataProviderCode';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCountryMapping(loadOptions);
		}
	});

	this.dataProviderShortNameHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'DataProviderPrimaryOrDefaultCountryGeographicalEntityShortName';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCountryMapping(loadOptions);
		}
	});

	this.dataProviderPrimaryOrDefaultCountryGeographicalEntityCountryAlpha2CodeHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'DataProviderPrimaryOrDefaultCountryGeographicalEntityCountryAlpha2Code';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCountryMapping(loadOptions);
		}
	});

	this.isDataProviderPrimaryOrDefaultCountryInconsistentMappingMessageHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'IsDataProviderPrimaryOrDefaultCountryInconsistentMappingMessage';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCountryMapping(loadOptions);
		}
	});

	this.mappingModeNameHeaderDataSource = [
		{
		text: 'New',
		value: 0
		},
		{
		text: 'Map',
		value: 1
		},
		{
		text: 'Save',
		value: 2
		},
		{
		text: 'Omit',
		value: 2
		}
	];

	this.countryAlpha2CodeNameHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'CountryAlpha2Code';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCountryMapping(loadOptions);
		}
	});

	this.reportListHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'ReportList';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCountryMapping(loadOptions);
		}
	});

	this.createdByHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'CreatedByUserFullNameSortableOrPrincipalName';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCountryMapping(loadOptions);
		}
	});

	this.modifiedByHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'ModifiedByUserFullNameSortableOrPrincipalName';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCountryMapping(loadOptions);
		}
	});

	this.reviewedByHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'ReviewedByUserFullNameSortableOrPrincipalName';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCountryMapping(loadOptions);
		}
	});

	this.createdOnHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'CreatedOn';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCountryMapping(loadOptions);
		}
	});

	this.modifiedOnHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'ModifiedOn';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCountryMapping(loadOptions);
		}
	});

	this.reviewedOnHeaderDataSource = new CustomStore({
		load: (loadOptions: any) => {
		loadOptions.column = 'ReviewedOn';
		loadOptions.valueColumn = '';

		return this.getHeaderFiltersCountryMapping(loadOptions);
		}
	});
  }

  omit(countryMappingId: any) {
	const mappingData: CountryMappingOption = {};
	mappingData.MappingModeId = MappingMode.Omitted;
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.CountryMappingId = countryMappingId;
	mappingData.ActionType = MappingAction.Omit;
	this.loaderService.show();
	this.countryDataService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshCompanyMappingDataGrid();
		});
  }

  save(countryMappingId: any) {
	const mappingData: CountryMappingOption = {};
	mappingData.MappingModeId = MappingMode.Saved;
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.CountryMappingId = countryMappingId;
	mappingData.ActionType = MappingAction.Save;
	this.loaderService.show();

	this.countryDataService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshCompanyMappingDataGrid();
		});
  }

  map(countryMappingId: any, dataProviderPrimaryOrDefaultCountryGeographicalEntityId: any) {
	const mappingData: CountryMappingOption = {};
	mappingData.MappingModeId = MappingMode.Mapped;
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.CountryMappingId = countryMappingId;
	mappingData.DataProviderPrimaryOrDefaultCountryGeographicalEntityId = dataProviderPrimaryOrDefaultCountryGeographicalEntityId;
	mappingData.ActionType = MappingAction.Map;
	this.loaderService.show();

	this.countryDataService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshCompanyMappingDataGrid();
		});
  }

  accept(countryMappingId) {
	const mappingData: CountryMappingOption = {};
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.CountryMappingId = countryMappingId;
	mappingData.ActionType = MappingAction.Accept;
	this.loaderService.show();
	this.countryDataService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshCompanyMappingDataGrid();
		});
  }

  review(countryMappingId) {
	const mappingData: CountryMappingOption = {};
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.CountryMappingId = countryMappingId;
	mappingData.ActionType = MappingAction.Review;
	this.loaderService.show();
	this.countryDataService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshCompanyMappingDataGrid();
		});
  }

  deactivate(countryMappingId: any) {
	const mappingData: CountryMappingOption = {};
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.CountryMappingId = countryMappingId;
	mappingData.ActionType = MappingAction.Deactivate;
	this.loaderService.show();
	this.countryDataService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshCompanyMappingDataGrid();
		});
  }

  reactivate(countryMappingId: any) {
	const mappingData: CountryMappingOption = {};
	mappingData.ModifiedByUserId = this.currentUser.UserId;
	mappingData.CountryMappingId = countryMappingId;
	mappingData.ActionType = MappingAction.Reactivate;
	this.loaderService.show();
	this.countryDataService.mappingAction(mappingData)
		.pipe(takeUntil(this._destroyed$))
		.subscribe((res) => {
		this.refreshCompanyMappingDataGrid();
		});
  }

  private getHeaderFiltersCountryMapping(loadingOption) {
	return this.countryDataService.getHeaderFiltersCompanyMapping(loadingOption)
		.toPromise()
		.catch(error => { throw new Error('Data Loading Error'); });
  }

  private loaderHandler() {
	if (!this.isCountryMappingDataLoading && !this.isCountryMasterDataGridSettingLoading) {
		this.loaderService.hide();
	}
  }

  private formatFilterOperators(rawFilter: any[]) {
	if (!rawFilter) {
		return null;
	}
	const filterData = this.filterBuilder.handleNotFilterFormatting(rawFilter);
	return this.filterBuilder.filterFormatting(filterData);
  }

  ngOnDestroy() {
	this.loaderService.hide();
	this._destroyed$.next(null);
	this._destroyed$.complete();
  }
}
